import React, { useState, useEffect, useRef } from 'react';
import styles from './InsertContentPopup.module.css';
import DOMPurify from 'dompurify';
import AlbumViewer from './AlbumViewer';
import RawHtmlEditor from './RawHtmlEditor';

export interface ExtensionProps {
  state: string;
  query: string;
  htmlStyle: string;
  imageHeight?: string;
  numImages?: string;
  linkText?: string;
  linkAlbumId?: string;
  imageUrl?: string;
  html?: string;
}

interface InsertContentPopupProps {
  onInsert: (extension: string, props: ExtensionProps) => void;
  onCancel: () => void;
  initialExtension?: string;
  initialProps?: ExtensionProps;
}

const InsertContentPopup: React.FC<InsertContentPopupProps> = ({
  onInsert,
  onCancel,
  initialExtension,
  initialProps,
}) => {
  const [selectedExtension, setSelectedExtension] = useState<
    'Count' | 'Link' | 'AlbumLink' | 'AlbumEmbed' | 'MyImage' | 'ImageList' | 'RecordBrowser' | 'RecordBrowserPopup' | 'RawHtml'
  >(
    (initialExtension as any) || 'Count'
  );

  const [props, setProps] = useState<ExtensionProps>(
    initialProps || {
      state: '',
      query: '',
      htmlStyle: '',
      imageHeight: '100',
      numImages: '100',
      linkText: '',
      linkAlbumId: '',
      imageUrl: '',
      html: '',
    }
  );

  // -------------------------------
  // Added for resizable panes
  // -------------------------------
  const [leftPaneWidth, setLeftPaneWidth] = useState<number>(300); // Initial width in pixels
  const isResizingRef = useRef(false);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    isResizingRef.current = true;
    // Start listening at the document level
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isResizingRef.current) return;
    // For a horizontal resize, we can set the leftPane width based on mouse X
    // Optional: add boundary checks or min/max widths
    setLeftPaneWidth(e.clientX);
  };

  const handleMouseUp = () => {
    isResizingRef.current = false;
    // Stop listening once mouse is released
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };
  // -------------------------------

  useEffect(() => {
    if (selectedExtension === 'Count') {
      // Example resetting some fields:
      setProps((prevProps) => ({
        ...prevProps,
        imageHeight: '100',
        linkText: '',
        linkAlbumId: '',
        imageUrl: '',
        numImages: '100',
        html: '',
      }));
    }
  }, [selectedExtension]);

  const handlePropChange = (prop: keyof ExtensionProps, value: string) => {
    let sanitizedValue = value;

    // Example sanitizing
    if (prop === 'htmlStyle') {
      sanitizedValue = DOMPurify.sanitize(value, { ALLOWED_ATTR: ['style'] });
    }
    if (prop === 'html') {
      sanitizedValue = DOMPurify.sanitize(value);
    }

    setProps((prevProps) => ({ ...prevProps, [prop]: sanitizedValue }));
  };

  const handlePropChangeRawHtml = (prop: keyof ExtensionProps, value: string) => {
    setProps((prevProps) => ({ ...prevProps, [prop]: value }));
  };

  const handleInsert = () => {
    onInsert(selectedExtension, props);
  };

  const renderExample = () => {
    if (selectedExtension === 'RawHtml') {
      // Render sanitized HTML
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(props.html || ''),
          }}
        />
      );
    } else {
      // For other extensions, show the custom element
      const attributes = Object.entries(props)
        .filter(([_, value]) => value != null && value !== '')
        .map(([key, value]) => {
          if (key === 'htmlStyle') {
            return `style="${value.replace(/"/g, '"')}"`;
          }
          const dataKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          let escapedValue = value;
          if (typeof value === 'object') {
            escapedValue = JSON.stringify(value).replace(/"/g, '"');
          } else if (typeof value === 'string') {
            escapedValue = value.replace(/"/g, '"');
          }
          return `data-${dataKey}="${escapedValue}"`;
        })
        .join(' ');

      const albumSource = `<${selectedExtension.toLowerCase()} ${attributes}></${selectedExtension.toLowerCase()}>`;
      return <AlbumViewer albumSource={albumSource} />;
    }
  };

  return (
    <div className={styles.popup}>
      <div className={styles.content}>
        {/* Left Pane */}
        <div
          className={styles.leftPane}
          style={{ width: leftPaneWidth }}
        >
          <select
            value={selectedExtension}
            onChange={(e) =>
              setSelectedExtension(e.target.value as typeof selectedExtension)
            }
            className={styles.combobox}
          >
            <option value="Count">Count</option>
            <option value="Link">Link</option>
            <option value="AlbumLink">Link to album</option>
            <option value="AlbumEmbed">Embed Album</option>
            <option value="MyImage">Image</option>
            <option value="ImageList">Image List</option>
            <option value="RecordBrowser">Record Browser</option>
            <option value="RecordBrowserPopup">Record Browser as popup</option>
            <option value="RawHtml">Raw Html</option>
          </select>

          <div className={styles.properties}>
            {selectedExtension === 'RawHtml' ? (
              <RawHtmlEditor
                initialHtml={props.html || ''}
                onChange={(html: string) => handlePropChangeRawHtml('html', html)}
              />
            ) : (
              <>
                {/* Normal property fields for other extensions */}
                {(selectedExtension !== 'AlbumLink' && selectedExtension !== 'AlbumEmbed') && (
                  <label>
                    State:
                    <input
                      type="text"
                      value={props.state}
                      onChange={(e) => handlePropChange('state', e.target.value)}
                    />
                  </label>
                )}
                {(selectedExtension !== 'AlbumLink' && selectedExtension !== 'AlbumEmbed') && (
                  <label>
                    HTML Style:
                    <input
                      type="text"
                      value={props.htmlStyle}
                      onChange={(e) => handlePropChange('htmlStyle', e.target.value)}
                      placeholder="e.g., color: green; font-weight: bold;"
                    />
                  </label>
                )}
                {selectedExtension === 'ImageList' && (
                  <>
                    <label>
                      Image Height:
                      <input
                        type="number"
                        value={props.imageHeight}
                        onChange={(e) => handlePropChange('imageHeight', e.target.value)}
                        min="1"
                      />
                    </label>
                    <label>
                      Number of Images:
                      <input
                        type="number"
                        value={props.numImages}
                        onChange={(e) => handlePropChange('numImages', e.target.value)}
                        min="1"
                      />
                    </label>
                  </>
                )}
                {(selectedExtension === 'Link' ||
                  selectedExtension === 'AlbumLink' ||
                  selectedExtension === 'RecordBrowserPopup') && (
                  <label>
                    Link text:
                    <input
                      type="text"
                      value={props.linkText}
                      onChange={(e) => handlePropChange('linkText', e.target.value)}
                    />
                  </label>
                )}
                {(selectedExtension === 'AlbumLink' ||
                  selectedExtension === 'AlbumEmbed') && (
                  <label>
                    Album Id:
                    <input
                      type="text"
                      value={props.linkAlbumId}
                      onChange={(e) => handlePropChange('linkAlbumId', e.target.value)}
                    />
                  </label>
                )}
                {selectedExtension === 'MyImage' && (
                  <label>
                    Image Url:
                    <input
                      type="text"
                      value={props.imageUrl}
                      onChange={(e) => handlePropChange('imageUrl', e.target.value)}
                    />
                  </label>
                )}
              </>
            )}
          </div>
        </div>

        {/* The Resizer */}
        <div
          className={styles.resizer}
          onMouseDown={handleMouseDown}
        />

        {/* Right Pane */}
        <div className={styles.rightPane}>
          <h3>Example:</h3>
          <div className={styles.scrollableExample}>{renderExample()}</div>
        </div>
      </div>

      <div className={styles.footer}>
        <button onClick={handleInsert} className={styles.insertButton}>
          Insert
        </button>
        <button onClick={onCancel} className={styles.cancelButton}>
          Cancel
        </button>
      </div>
    </div>
  );
};

export default InsertContentPopup;